import { DndContext, DragOverlay } from '@dnd-kit/core';
import { GroveMoneyMapCalculatorButton } from '@fruitful/earnings-calculator';
import {
    getCategorizedRulesAndTotals,
    prioritySortRulesAndGroups,
    stripMoneyMapAccountFromRules,
    updateRuleAndGroupPriorities,
} from '@fruitful/money-map';
import {
    type DeepPartial,
    type MoneyMapRuleGroup as MoneyMapRuleGroupType,
    MoneyMapStatus,
    type MoneyMapUpsertDto,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
    type BadgeLabelVariant,
    CirclePlusIcon,
    FullScreenSpinner,
    IconButton,
    PanelRightIcon,
    TooltipGroupProvider,
    TooltipTrigger,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import FullScreenError from '../../common/components/FullScreenError';
import { Button } from '../../common/components/GroveButtons';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { formatToSimpleDateTimeString } from '../../common/utils/formatting';
import { selectedMembershipAtom } from '../../entities/entityAtoms';
import type { MembershipDetails } from '../../entities/entityTypes';
import { TrackedEvent } from '../../logger/Events';
import { captureException, trackEvent } from '../../logger/Logger';
import { useDeleteMoneyMapRule } from '../hooks/useDeleteMoneyMapRule';
import { useDeleteMoneyMapRuleGroup } from '../hooks/useDeleteMoneyMapRuleGroup';
import { getMoneyMapQueryKey, useGetMoneyMap } from '../hooks/useGetMoneyMap';
import {
    useFinalizeMoneyMap,
    usePublishMoneyMap,
} from '../hooks/useUpdateMoneyMapStatus';
import { useUpsertMoneyMap } from '../hooks/useUpsertMoneyMap';
import { MoneyMapPublishFinalizeEventSource } from '../moneyMapPublishFinalizeTracking';
import { useMoneyMapAccounts } from '../providers/MoneyMapAccountsProvider';
import { getMoneyMapperEffectiveMonthlyIncomeCents } from '../utils/moneyMapperEffectiveIncome';
import {
    cleanAttributesForUpsert,
    getMoneyMapValidationMessage,
    isMoneyMapEditable,
    isMoneyMapValidForPreviewOrActiveStatus,
    omitRoundTrippedMoneyMapAccounts,
} from '../utils/moneyMapUtils';
import GoalForm from './forms/GoalForm';
import { GoalGroupEditorPanel } from './goals-waterfall/GoalGroupEditorPanel';
import {
    GOAL_PRESET_PANEL_WIDTH,
    GoalPresetsPanel,
    PresetTileDragOverlay,
} from './goals-waterfall/GoalPresetsPanel';
import type { GoalSelectionState } from './goals-waterfall/goalsWaterfallTypes';
import { useMoneyMapperGoalsDnD } from './goals-waterfall/useMoneyMapperGoalsDnD';
import { MoneyMapperDiagramPanel } from './MoneyMapperDiagramPanel';
import { MoneyMapperInputPanel } from './MoneyMapperInputPanel';
import { MoneyMapperLeftNav } from './MoneyMapperLeftNav';
import {
    type MoneyMapperPrimarySection,
    type MoneyMapperYourMoneySubSegment,
} from './moneyMapperNavigation';
import { MoneyMapperNavMapFooter } from './MoneyMapperNavMapFooter';
import { MoneyMapRenameModal } from './MoneyMapRenameModal';
import { MoneyMapSimulatorButton } from './MoneyMapSimulatorButton';
import MoneyMapStatusChangeConfirmModal from './MoneyMapStatusChangeConfirmModal';

interface MoneyMapperPageProps {
    moneyMapId: string;
}

const getEditMoneyMapNameModalTitle = (
    membership: MembershipDetails | null,
): string => {
    const primaryMemberName = membership?.users?.[0]?.firstName ?? '';
    const partnerMemberName = membership?.users?.[1]?.firstName ?? '';

    if (primaryMemberName.length > 0 && partnerMemberName.length > 0) {
        return `Edit ${primaryMemberName} and ${partnerMemberName}'s Money Map name`;
    } else if (primaryMemberName.length > 0) {
        return `Edit ${primaryMemberName}'s Money Map name`;
    } else {
        return 'Edit Money Map name';
    }
};

const MONEY_MAP_VISUAL_PANEL_WIDTH = 480;

export const MoneyMapperPage = ({ moneyMapId }: MoneyMapperPageProps) => {
    const toast = useToastController();
    const queryClient = useQueryClient();
    const membership = useAtomValue(selectedMembershipAtom);
    const [isLeftNavExpanded, setIsLeftNavExpanded] = useState(true);
    const [isMoneyMapVisualOpen, setIsMoneyMapVisualOpen] = useState(true);
    const [isGoalPresetPanelOpen, setIsGoalPresetPanelOpen] = useState(false);
    const [goalSelection, setGoalSelection] =
        useState<GoalSelectionState>(null);
    const [mapperPrimary, setMapperPrimary] =
        useState<MoneyMapperPrimarySection>('your-money');
    const [mapperYourMoneySub, setMapperYourMoneySub] =
        useState<MoneyMapperYourMoneySubSegment>('take-home-pay');
    const reportedFailedMoneyMapId = useRef<string | null>(null);

    // Close the goal preset panel when navigating away from goals
    useEffect(() => {
        if (mapperPrimary !== 'goals') {
            setIsGoalPresetPanelOpen(false);
            setGoalSelection(null);
        }
    }, [mapperPrimary]);

    const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isSavingMoneyMapTitle, setIsSavingMoneyMapTitle] = useState(false);
    const { accounts, refetch: refetchMoneyMapAccounts } =
        useMoneyMapAccounts();
    const { setMoneyMapToPreviewAsync } = usePublishMoneyMap();
    const { setMoneyMapToActiveAsync } = useFinalizeMoneyMap();
    const [isUpdatingMoneyMapStatus, setIsUpdatingMoneyMapStatus] =
        useState(false);
    const { mutateAsync: upsertMoneyMap, isPending: isSavingMoneyMap } =
        useUpsertMoneyMap();
    /** Same API; separate hook instance so monthly-income-only sync does not flip `isSavingMoneyMap`. */
    const { mutateAsync: upsertMoneyMapMonthlyIncome } = useUpsertMoneyMap();
    const { mutateAsync: deleteMoneyMapRule } = useDeleteMoneyMapRule();
    const { mutateAsync: deleteMoneyMapRuleGroup } =
        useDeleteMoneyMapRuleGroup();

    const {
        data: fetchedMoneyMap,
        error: moneyMapError,
        isError: isMoneyMapError,
        isLoading: isLoadingMoneyMap,
        refetch: refetchMoneyMap,
    } = useGetMoneyMap({ id: moneyMapId }, { enabled: moneyMapId != null });

    useEffect(() => {
        if (isLoadingMoneyMap) {
            return;
        }

        if (fetchedMoneyMap != null) {
            reportedFailedMoneyMapId.current = null;
            return;
        }

        if (reportedFailedMoneyMapId.current === moneyMapId) {
            return;
        }

        reportedFailedMoneyMapId.current = moneyMapId;

        if (isMoneyMapError) {
            captureException(moneyMapError);
        }

        toast.show(
            'Money map could not be loaded',
            MutationErrorToastOptions(),
        );
    }, [
        fetchedMoneyMap,
        isLoadingMoneyMap,
        isMoneyMapError,
        moneyMapError,
        moneyMapId,
        toast,
    ]);

    const publishFinalizeEnabled = useMemo(
        () =>
            fetchedMoneyMap != null
                ? isMoneyMapValidForPreviewOrActiveStatus(fetchedMoneyMap)
                : false,
        [fetchedMoneyMap],
    );

    const publishFinalizeDisabledMessage = useMemo(
        () =>
            fetchedMoneyMap != null
                ? getMoneyMapValidationMessage(fetchedMoneyMap)
                : null,
        [fetchedMoneyMap],
    );

    const {
        goalRules: presetGoalRules,
        billRules: presetBillRules,
        spendRules: presetSpendRules,
        billsTotalCents: presetBillsTotalCents,
        spendTotalCents: presetSpendTotalCents,
    } = useMemo(
        () =>
            fetchedMoneyMap != null
                ? getCategorizedRulesAndTotals(fetchedMoneyMap, accounts)
                : {
                      goalRules: [],
                      billRules: [],
                      spendRules: [],
                      billsTotalCents: 0,
                      spendTotalCents: 0,
                  },
        [JSON.stringify(fetchedMoneyMap), JSON.stringify(accounts)],
    );

    const presetGoalItems = useMemo(
        () =>
            prioritySortRulesAndGroups(
                presetGoalRules,
                fetchedMoneyMap?.moneyMapRuleGroups ?? [],
            ),
        [presetGoalRules, fetchedMoneyMap?.moneyMapRuleGroups],
    );

    type UpsertMoneyMapFn = typeof upsertMoneyMap;

    const persistMoneyMapUpdatesWithUpsert = useCallback(
        async (
            updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
            upsert: UpsertMoneyMapFn,
        ): Promise<boolean> => {
            const moneyMapQueryKey = [getMoneyMapQueryKey({ id: moneyMapId })];
            const previousMoneyMap =
                queryClient.getQueryData<MoneyMapWithRulesAndAccounts>(
                    moneyMapQueryKey,
                );

            if (previousMoneyMap == null) {
                return false;
            }

            // ENG-7083: do not spread the cached map. That sent Core and Spend
            // account objects on every save, and the API created new ones.
            // Required fields, then this update. Unchanged accounts are reduced
            // to ids inside omitRoundTrippedMoneyMapAccounts.
            const nextMoneyMap = omitRoundTrippedMoneyMapAccounts(
                {
                    id: previousMoneyMap.id,
                    membershipId: previousMoneyMap.membershipId,
                    title: previousMoneyMap.title,
                    status: previousMoneyMap.status,
                    monthlyIncomeCents: previousMoneyMap.monthlyIncomeCents,
                    incomeMoneyMapAccountId:
                        previousMoneyMap.incomeMoneyMapAccountId,
                    ...updates,
                },
                previousMoneyMap,
            );

            // Cancel any in-flight GET so a stale response can't overwrite the
            // result we're about to receive from the server.
            await queryClient.cancelQueries({
                queryKey: moneyMapQueryKey,
                exact: true,
            });

            try {
                await upsert({
                    body: cleanAttributesForUpsert(
                        nextMoneyMap,
                    ) as MoneyMapUpsertDto,
                });
                return true;
            } catch (error) {
                captureException(error);
                toast.show(
                    'Error saving money map',
                    MutationErrorToastOptions(error),
                );
                return false;
            } finally {
                // Always refetch so every caller works from authoritative server
                // data, preventing stale priorities from corrupting subsequent saves.
                await queryClient.refetchQueries({
                    queryKey: moneyMapQueryKey,
                    exact: true,
                });
                // Goal/account forms resolve proposed IM accounts from membership
                // accounts (`useMoneyMapAccounts`) — refetch so Apex risk, portfolio,
                // etc. match the DB after save.
                await refetchMoneyMapAccounts();
            }
        },
        [moneyMapId, queryClient, refetchMoneyMapAccounts, toast],
    );

    const persistMoneyMapUpdates = useCallback(
        async (
            updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
        ): Promise<boolean> => {
            return await persistMoneyMapUpdatesWithUpsert(
                updates,
                upsertMoneyMap,
            );
        },
        [persistMoneyMapUpdatesWithUpsert, upsertMoneyMap],
    );

    const persistMonthlyIncomeCentsToMoneyMap = useCallback(
        async (monthlyIncomeCents: number): Promise<boolean> => {
            return await persistMoneyMapUpdatesWithUpsert(
                { monthlyIncomeCents },
                upsertMoneyMapMonthlyIncome,
            );
        },
        [persistMoneyMapUpdatesWithUpsert, upsertMoneyMapMonthlyIncome],
    );

    const onUpdateMoneyMap = useCallback(
        async (
            updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
        ): Promise<boolean> => {
            return await persistMoneyMapUpdates(updates);
        },
        [persistMoneyMapUpdates],
    );

    const onPressPublish = useCallback(async () => {
        setIsUpdatingMoneyMapStatus(true);
        try {
            await setMoneyMapToPreviewAsync(moneyMapId);
            toast.show('Money map published!', MutationSuccessToastOptions);
            trackEvent(TrackedEvent.MoneyMapPublishedForPreview, {
                moneyMapId,
                membershipId: membership?.id ?? '',
                source: MoneyMapPublishFinalizeEventSource.MAPPER,
            });
        } catch (error) {
            captureException(error);
            toast.show(
                'Error publishing money map.',
                MutationErrorToastOptions(error),
            );
        } finally {
            await refetchMoneyMap();
            setIsUpdatingMoneyMapStatus(false);
        }
    }, [
        membership?.id,
        moneyMapId,
        refetchMoneyMap,
        setMoneyMapToPreviewAsync,
        toast,
    ]);

    const onPressFinalize = useCallback(async () => {
        setIsUpdatingMoneyMapStatus(true);
        try {
            await setMoneyMapToActiveAsync(moneyMapId);
            toast.show('Money map finalized!', MutationSuccessToastOptions);
            trackEvent(TrackedEvent.MoneyMapFinalizedForAction, {
                moneyMapId,
                membershipId: membership?.id ?? '',
                source: MoneyMapPublishFinalizeEventSource.MAPPER,
            });
        } catch (error) {
            captureException(error);
            toast.show(
                'Error finalizing money map.',
                MutationErrorToastOptions(error),
            );
        } finally {
            await refetchMoneyMap();
            setIsUpdatingMoneyMapStatus(false);
        }
    }, [
        membership?.id,
        moneyMapId,
        refetchMoneyMap,
        setMoneyMapToActiveAsync,
        toast,
    ]);

    const onRemoveRule = useCallback(
        async (
            ruleId: string,
            options?: { onBeforePanelClose?: () => void },
        ) => {
            try {
                const allRules = fetchedMoneyMap?.moneyMapRules ?? [];
                const deletedRule = allRules.find((rule) => rule.id === ruleId);
                const groupId = deletedRule?.moneyMapRuleGroupId;
                const rulesInSameGroup =
                    groupId != null
                        ? allRules.filter(
                              (r) => r.moneyMapRuleGroupId === groupId,
                          )
                        : [];
                const shouldDeleteEmptyGroup =
                    groupId != null && rulesInSameGroup.length === 1;

                await deleteMoneyMapRule({ urlParams: { id: ruleId } });

                if (shouldDeleteEmptyGroup) {
                    await deleteMoneyMapRuleGroup({
                        urlParams: { id: groupId },
                    });
                }

                options?.onBeforePanelClose?.();

                if (shouldDeleteEmptyGroup) {
                    const deletedGroup = (
                        fetchedMoneyMap?.moneyMapRuleGroups ?? []
                    ).find((g) => g.id === groupId);

                    if (deletedGroup != null) {
                        const existingRules = stripMoneyMapAccountFromRules(
                            fetchedMoneyMap?.moneyMapRules ?? [],
                        );

                        const { rules: updatedRules, groups: updatedGroups } =
                            updateRuleAndGroupPriorities(
                                deletedGroup,
                                existingRules,
                                fetchedMoneyMap?.moneyMapRuleGroups ?? [],
                                'delete',
                            );

                        await onUpdateMoneyMap({
                            moneyMapRules: updatedRules,
                            moneyMapRuleGroups: updatedGroups,
                        });
                    }
                } else if (deletedRule != null) {
                    const existingRules = stripMoneyMapAccountFromRules(
                        fetchedMoneyMap?.moneyMapRules ?? [],
                    );

                    const { rules: updatedRules, groups: updatedGroups } =
                        updateRuleAndGroupPriorities(
                            deletedRule,
                            existingRules,
                            fetchedMoneyMap?.moneyMapRuleGroups ?? [],
                            'delete',
                        );

                    await onUpdateMoneyMap({
                        moneyMapRules: updatedRules,
                        moneyMapRuleGroups: updatedGroups,
                    });
                }

                await Promise.all([
                    refetchMoneyMap(),
                    refetchMoneyMapAccounts(),
                ]);
            } catch (error) {
                captureException(error);
                toast.show(
                    'Error deleting money map rule',
                    MutationErrorToastOptions(error),
                );
            }
        },
        [
            deleteMoneyMapRule,
            deleteMoneyMapRuleGroup,
            fetchedMoneyMap?.moneyMapRuleGroups,
            fetchedMoneyMap?.moneyMapRules,
            onUpdateMoneyMap,
            refetchMoneyMap,
            refetchMoneyMapAccounts,
            toast,
        ],
    );

    const onRemoveGroup = useCallback(
        async (
            groupId: string,
            options?: { onBeforePanelClose?: () => void },
        ) => {
            try {
                await deleteMoneyMapRuleGroup({ urlParams: { id: groupId } });

                options?.onBeforePanelClose?.();

                const deletedGroup = (
                    fetchedMoneyMap?.moneyMapRuleGroups ?? []
                ).find((group) => group.id === groupId);

                if (deletedGroup != null) {
                    // Exclude existing moneyMapAccount dto object from upsert call.
                    const existingRules = stripMoneyMapAccountFromRules(
                        fetchedMoneyMap?.moneyMapRules ?? [],
                    );

                    // Update priorities across all rules and groups
                    const { rules: updatedRules, groups: updatedGroups } =
                        updateRuleAndGroupPriorities(
                            deletedGroup,
                            existingRules,
                            fetchedMoneyMap?.moneyMapRuleGroups ?? [],
                            'delete',
                        );

                    await onUpdateMoneyMap({
                        moneyMapRules: updatedRules,
                        moneyMapRuleGroups: updatedGroups,
                    });
                }

                await refetchMoneyMapAccounts();
            } catch (error) {
                captureException(error);
                toast.show(
                    'Error deleting money map group',
                    MutationErrorToastOptions(error),
                );
            }
        },
        [
            deleteMoneyMapRuleGroup,
            fetchedMoneyMap?.moneyMapRuleGroups,
            fetchedMoneyMap?.moneyMapRules,
            onUpdateMoneyMap,
            refetchMoneyMapAccounts,
            toast,
        ],
    );

    const presetMonthlyExpensesCents = useMemo(
        () => (presetBillsTotalCents ?? 0) + (presetSpendTotalCents ?? 0),
        [presetBillsTotalCents, presetSpendTotalCents],
    );

    const presetNonGoalRuleCount = useMemo(
        () => presetBillRules.length + presetSpendRules.length,
        [presetBillRules.length, presetSpendRules.length],
    );

    const getLatestMoneyMapGroupsForWaterfall = useCallback((): Array<
        DeepPartial<MoneyMapRuleGroupType>
    > => {
        const latest = queryClient.getQueryData<MoneyMapWithRulesAndAccounts>([
            getMoneyMapQueryKey({ id: moneyMapId }),
        ]);
        return latest?.moneyMapRuleGroups ?? [];
    }, [queryClient, moneyMapId]);

    const {
        goalsWaterfallDnd,
        activePreset,
        presetPreviewInsertIndex,
        presetPreviewPreset,
        onDragStart,
        onDragMove,
        onDragEnd,
        onDragCancel,
        collisionDetectionStrategy,
    } = useMoneyMapperGoalsDnD({
        goalRules: presetGoalRules,
        goalItems: presetGoalItems,
        groups: fetchedMoneyMap?.moneyMapRuleGroups ?? [],
        moneyMap: fetchedMoneyMap ?? {},
        membership,
        monthlyExpensesCents: presetMonthlyExpensesCents,
        nonGoalRuleCount: presetNonGoalRuleCount,
        onUpdateMoneyMap,
        onDeleteGroup: async (groupId: string) => {
            await deleteMoneyMapRuleGroup({ urlParams: { id: groupId } });
        },
        onDeleteGoal: onRemoveRule,
        refetchMoneyMap,
        getLatestMoneyMapGroups: getLatestMoneyMapGroupsForWaterfall,
    });

    const getMoneyMapStatusBadgeVariant = (
        status?: string | null,
    ): BadgeLabelVariant => {
        switch (status) {
            case 'ACTIVE':
                return 'positive';
            case 'PREVIEW':
                return 'warning';
            case 'INACTIVE':
            case 'DRAFT':
            default:
                return 'neutral';
        }
    };

    const getMoneyMapTitle = (
        membership: MembershipDetails | null,
        updatedAt?: string | Date | null,
    ): string => {
        const names =
            membership?.users
                ?.map(({ firstName, lastName }) =>
                    `${firstName?.trim() ?? ''} ${lastName?.trim() ?? ''}`.trim(),
                )
                .filter((name) => name.length > 0) ?? [];

        const formattedDate = formatToSimpleDateTimeString(updatedAt);
        const formattedDateSuffix =
            formattedDate.length > 0 ? ` (${formattedDate})` : '';

        if (names.length === 1) {
            return `${names[0]}'s Money Map${formattedDateSuffix}`;
        }

        if (names.length === 2) {
            return `${names[0]} and ${names[1]}'s Money Map${formattedDateSuffix}`;
        }

        return `Money Map${formattedDateSuffix}`;
    };

    const saveMoneyMapTitle = useCallback(
        async (title: string): Promise<boolean> => {
            setIsSavingMoneyMapTitle(true);
            try {
                const ok = await persistMoneyMapUpdates({ title });
                if (ok) {
                    toast.show(
                        'Money map name saved',
                        MutationSuccessToastOptions,
                    );
                }
                return ok;
            } finally {
                setIsSavingMoneyMapTitle(false);
            }
        },
        [persistMoneyMapUpdates, toast],
    );

    const moneyMapForEarningsCalculator = useMemo(() => {
        if (fetchedMoneyMap == null) {
            return null;
        }
        const effectiveMonthlyIncomeCents =
            getMoneyMapperEffectiveMonthlyIncomeCents({
                moneyMap: fetchedMoneyMap,
                membershipIncomes: membership?.membershipIncomes,
            });
        return {
            ...fetchedMoneyMap,
            monthlyIncomeCents: effectiveMonthlyIncomeCents,
        };
    }, [fetchedMoneyMap, membership?.membershipIncomes]);

    if (isLoadingMoneyMap) {
        return <FullScreenSpinner />;
    }

    if (fetchedMoneyMap == null) {
        return <FullScreenError message="Money map could not be loaded." />;
    }

    const persistedTitle = (fetchedMoneyMap.title ?? '').trim();
    const displayMoneyMapTitle =
        persistedTitle.length > 0
            ? persistedTitle
            : getMoneyMapTitle(membership, fetchedMoneyMap.updatedAt);

    return (
        <DndContext
            sensors={goalsWaterfallDnd.sensors}
            collisionDetection={collisionDetectionStrategy}
            onDragStart={onDragStart}
            onDragMove={onDragMove}
            onDragEnd={(event) => {
                void onDragEnd(event);
            }}
            onDragCancel={onDragCancel}
        >
            <View style={{ width: '100%', flex: 1, position: 'relative' }}>
                <XStack
                    flex={1}
                    width="100%"
                    minHeight={0}
                    alignItems="stretch"
                >
                    <MoneyMapperLeftNav
                        isExpanded={isLeftNavExpanded}
                        onToggleExpanded={() => {
                            setIsLeftNavExpanded((v) => !v);
                        }}
                        primarySection={mapperPrimary}
                        onPrimarySectionChange={setMapperPrimary}
                        calculator={
                            membership?.holisticSummary != null &&
                            moneyMapForEarningsCalculator != null ? (
                                <GroveMoneyMapCalculatorButton
                                    layout={
                                        isLeftNavExpanded
                                            ? 'sidebarExpanded'
                                            : 'sidebarCollapsed'
                                    }
                                    moneyMap={moneyMapForEarningsCalculator}
                                    holisticSummary={membership.holisticSummary}
                                    appContext="grove-money-mapper"
                                    onInteractionEvent={(eventName, data) => {
                                        trackEvent(
                                            eventName as TrackedEvent,
                                            data,
                                        );
                                    }}
                                />
                            ) : null
                        }
                        simulator={
                            <MoneyMapSimulatorButton
                                moneyMap={fetchedMoneyMap}
                                accounts={accounts}
                            />
                        }
                        mapFooter={
                            <MoneyMapperNavMapFooter
                                isExpanded={isLeftNavExpanded}
                                displayMoneyMapTitle={displayMoneyMapTitle}
                                canEditName={isMoneyMapEditable(
                                    fetchedMoneyMap,
                                )}
                                onPressEditName={() => {
                                    setIsRenameModalOpen(true);
                                }}
                                statusText={fetchedMoneyMap.status ?? ''}
                                statusBadgeVariant={getMoneyMapStatusBadgeVariant(
                                    fetchedMoneyMap.status,
                                )}
                                updatedAt={fetchedMoneyMap.updatedAt}
                                isSavingMoneyMap={isSavingMoneyMap}
                            />
                        }
                    />
                    <XStack
                        flex={1}
                        minWidth={0}
                        minHeight={0}
                        alignItems="stretch"
                        overflow="hidden"
                        style={{ position: 'relative' }}
                    >
                        <YStack flex={1} minWidth={0} minHeight={0}>
                            <XStack flex={1} minHeight={0} overflow="hidden">
                                <MoneyMapperInputPanel
                                    headerTrailing={
                                        <>
                                            {mapperPrimary === 'goals' && (
                                                <Button
                                                    variant="secondary"
                                                    size="small"
                                                    icon={CirclePlusIcon}
                                                    iconPosition="left"
                                                    onPress={() => {
                                                        // If a goal/group editor panel is open it sits on top of the
                                                        // presets panel — close it so the presets panel becomes visible.
                                                        if (
                                                            goalSelection !=
                                                            null
                                                        ) {
                                                            setGoalSelection(
                                                                null,
                                                            );
                                                            setIsGoalPresetPanelOpen(
                                                                true,
                                                            );
                                                            setIsMoneyMapVisualOpen(
                                                                true,
                                                            );
                                                            return;
                                                        }
                                                        setIsGoalPresetPanelOpen(
                                                            (open) => {
                                                                if (!open) {
                                                                    setIsMoneyMapVisualOpen(
                                                                        true,
                                                                    );
                                                                }
                                                                return !open;
                                                            },
                                                        );
                                                    }}
                                                >
                                                    Add goal
                                                </Button>
                                            )}
                                            {!isMoneyMapVisualOpen && (
                                                <TooltipTrigger
                                                    tooltipContent="Show Money Map"
                                                    tooltipGroupName="money-mapper-header"
                                                >
                                                    <IconButton
                                                        variant="tertiary"
                                                        size="tiny"
                                                        icon={PanelRightIcon}
                                                        onPress={() => {
                                                            setIsMoneyMapVisualOpen(
                                                                (open) => !open,
                                                            );
                                                        }}
                                                    />
                                                </TooltipTrigger>
                                            )}
                                            {mapperPrimary === 'accounts' &&
                                                fetchedMoneyMap.status ===
                                                    MoneyMapStatus.DRAFT &&
                                                (publishFinalizeDisabledMessage ===
                                                    '' ||
                                                publishFinalizeDisabledMessage ===
                                                    null ? (
                                                    <Button
                                                        variant="primary"
                                                        size="tiny"
                                                        onPress={() => {
                                                            setIsConfirmModalOpen(
                                                                true,
                                                            );
                                                        }}
                                                        disabled={
                                                            !publishFinalizeEnabled ||
                                                            isUpdatingMoneyMapStatus
                                                        }
                                                    >
                                                        Publish
                                                    </Button>
                                                ) : (
                                                    <TooltipTrigger
                                                        tooltipContent={
                                                            publishFinalizeDisabledMessage ??
                                                            ''
                                                        }
                                                        tooltipGroupName="money-mapper-header"
                                                    >
                                                        <Button
                                                            variant="primary"
                                                            size="tiny"
                                                            onPress={() => {
                                                                setIsConfirmModalOpen(
                                                                    true,
                                                                );
                                                            }}
                                                            disabled={
                                                                !publishFinalizeEnabled ||
                                                                isUpdatingMoneyMapStatus
                                                            }
                                                        >
                                                            Publish
                                                        </Button>
                                                    </TooltipTrigger>
                                                ))}
                                            {mapperPrimary === 'accounts' &&
                                                fetchedMoneyMap.status ===
                                                    MoneyMapStatus.PREVIEW &&
                                                (publishFinalizeDisabledMessage ===
                                                    '' ||
                                                publishFinalizeDisabledMessage ===
                                                    null ? (
                                                    <Button
                                                        variant="primary"
                                                        size="tiny"
                                                        onPress={() => {
                                                            setIsConfirmModalOpen(
                                                                true,
                                                            );
                                                        }}
                                                        disabled={
                                                            !publishFinalizeEnabled ||
                                                            isUpdatingMoneyMapStatus
                                                        }
                                                    >
                                                        Finalize
                                                    </Button>
                                                ) : (
                                                    <TooltipTrigger
                                                        tooltipContent={
                                                            publishFinalizeDisabledMessage ??
                                                            ''
                                                        }
                                                        tooltipGroupName="money-mapper-header"
                                                    >
                                                        <Button
                                                            variant="primary"
                                                            size="tiny"
                                                            onPress={() => {
                                                                setIsConfirmModalOpen(
                                                                    true,
                                                                );
                                                            }}
                                                            disabled={
                                                                !publishFinalizeEnabled ||
                                                                isUpdatingMoneyMapStatus
                                                            }
                                                        >
                                                            Finalize
                                                        </Button>
                                                    </TooltipTrigger>
                                                ))}
                                        </>
                                    }
                                    primarySection={mapperPrimary}
                                    yourMoneySubSegment={mapperYourMoneySub}
                                    onYourMoneySubSegmentChange={(sub) => {
                                        setMapperYourMoneySub(sub);
                                    }}
                                    isSaving={isSavingMoneyMap}
                                    moneyMap={fetchedMoneyMap}
                                    onUpdateMoneyMap={onUpdateMoneyMap}
                                    persistMonthlyIncomeCentsToMoneyMap={
                                        persistMonthlyIncomeCentsToMoneyMap
                                    }
                                    onRemoveRule={onRemoveRule}
                                    onRemoveGroup={onRemoveGroup}
                                    refetchMoneyMap={refetchMoneyMap}
                                    onGoalSelected={(selection) => {
                                        setGoalSelection(selection);
                                        if (selection != null) {
                                            setIsMoneyMapVisualOpen(true);
                                        }
                                    }}
                                    selectedGoal={goalSelection}
                                    goalsWaterfallDnd={goalsWaterfallDnd}
                                    presetPreviewInsertIndex={
                                        presetPreviewInsertIndex
                                    }
                                    presetPreviewPreset={presetPreviewPreset}
                                />
                            </XStack>
                        </YStack>
                        <View
                            animation="slideScreen"
                            animateOnly={['width']}
                            width={
                                isMoneyMapVisualOpen
                                    ? isGoalPresetPanelOpen ||
                                      goalSelection != null
                                        ? GOAL_PRESET_PANEL_WIDTH
                                        : MONEY_MAP_VISUAL_PANEL_WIDTH
                                    : 0
                            }
                            flexShrink={0}
                            alignSelf="stretch"
                            minHeight={0}
                            overflow="hidden"
                            backgroundColor="$white"
                        >
                            <YStack
                                width={
                                    isGoalPresetPanelOpen ||
                                    goalSelection != null
                                        ? GOAL_PRESET_PANEL_WIDTH
                                        : MONEY_MAP_VISUAL_PANEL_WIDTH
                                }
                                flex={1}
                                minHeight={0}
                                height="100%"
                                borderLeftWidth={1}
                                borderLeftColor="rgba(0,0,0,0.06)"
                                backgroundColor="$white"
                            >
                                <MoneyMapperDiagramPanel
                                    moneyMap={fetchedMoneyMap}
                                    accounts={accounts}
                                    onPressClose={() => {
                                        setIsMoneyMapVisualOpen(false);
                                    }}
                                />
                            </YStack>
                        </View>
                        <YStack
                            animation="slideScreen"
                            animateOnly={['transform']}
                            position="absolute"
                            top={0}
                            right={0}
                            bottom={0}
                            width={GOAL_PRESET_PANEL_WIDTH}
                            transform={[
                                {
                                    translateX: isGoalPresetPanelOpen
                                        ? 0
                                        : GOAL_PRESET_PANEL_WIDTH,
                                },
                            ]}
                            overflow="hidden"
                            backgroundColor="$white"
                            zIndex={20}
                            shadowColor="$black"
                            shadowOffset={{ width: -4, height: 0 }}
                            shadowOpacity={0.08}
                            shadowRadius={16}
                        >
                            <GoalPresetsPanel
                                onClose={() => {
                                    setIsGoalPresetPanelOpen(false);
                                }}
                            />
                        </YStack>
                        {/* Goal editor panel — sits on top of the presets panel */}
                        <YStack
                            animation="slideScreen"
                            animateOnly={['transform']}
                            position="absolute"
                            top={0}
                            right={0}
                            bottom={0}
                            width={GOAL_PRESET_PANEL_WIDTH}
                            transform={[
                                {
                                    translateX:
                                        goalSelection != null
                                            ? 0
                                            : GOAL_PRESET_PANEL_WIDTH,
                                },
                            ]}
                            overflow="hidden"
                            backgroundColor="$white"
                            zIndex={21}
                            shadowColor="$black"
                            shadowOffset={{ width: -4, height: 0 }}
                            shadowOpacity={0.08}
                            shadowRadius={16}
                        >
                            {goalSelection?.type === 'goal' &&
                                (() => {
                                    const rule = (
                                        fetchedMoneyMap.moneyMapRules ?? []
                                    ).find(
                                        (r) => r.id === goalSelection.goalId,
                                    );
                                    if (rule == null) return null;
                                    const group =
                                        goalSelection.groupId != null
                                            ? (
                                                  fetchedMoneyMap.moneyMapRuleGroups ??
                                                  []
                                              ).find(
                                                  (g) =>
                                                      g.id ===
                                                      goalSelection.groupId,
                                              )
                                            : undefined;
                                    return (
                                        <GoalForm
                                            key={goalSelection.goalId}
                                            rule={rule}
                                            group={group}
                                            moneyMap={fetchedMoneyMap}
                                            priority={rule.priority ?? 0}
                                            onPressSave={async (updates) => {
                                                await onUpdateMoneyMap(updates);
                                                setGoalSelection(null);
                                            }}
                                            onPressRemove={async (ruleId) => {
                                                if (ruleId != null) {
                                                    await onRemoveRule(ruleId, {
                                                        onBeforePanelClose:
                                                            () => {
                                                                setGoalSelection(
                                                                    null,
                                                                );
                                                            },
                                                    });
                                                }
                                            }}
                                            onClose={() => {
                                                setGoalSelection(null);
                                            }}
                                            presentation="inline"
                                        />
                                    );
                                })()}
                            {goalSelection?.type === 'group' &&
                                (() => {
                                    const group = (
                                        fetchedMoneyMap.moneyMapRuleGroups ?? []
                                    ).find(
                                        (g) => g.id === goalSelection.groupId,
                                    );
                                    if (group == null) return null;
                                    const groupRules = (
                                        fetchedMoneyMap.moneyMapRules ?? []
                                    ).filter(
                                        (r) =>
                                            r.moneyMapRuleGroupId === group.id,
                                    );
                                    return (
                                        <GoalGroupEditorPanel
                                            key={goalSelection.groupId}
                                            group={group}
                                            groupRules={groupRules}
                                            moneyMap={fetchedMoneyMap}
                                            onClose={() => {
                                                setGoalSelection(null);
                                            }}
                                            onSave={async (updates) => {
                                                const success =
                                                    await onUpdateMoneyMap(
                                                        updates,
                                                    );
                                                if (success) {
                                                    setGoalSelection(null);
                                                }
                                                return success;
                                            }}
                                            onDelete={async () => {
                                                if (group.id != null) {
                                                    await onRemoveGroup(
                                                        group.id,
                                                        {
                                                            onBeforePanelClose:
                                                                () => {
                                                                    setGoalSelection(
                                                                        null,
                                                                    );
                                                                },
                                                        },
                                                    );
                                                    toast.show(
                                                        'Goal set deleted',
                                                        MutationSuccessToastOptions,
                                                    );
                                                }
                                            }}
                                        />
                                    );
                                })()}
                        </YStack>
                    </XStack>
                </XStack>
                <MoneyMapRenameModal
                    isOpen={isRenameModalOpen}
                    onClose={() => {
                        setIsRenameModalOpen(false);
                    }}
                    modalTitle={getEditMoneyMapNameModalTitle(membership)}
                    currentTitle={displayMoneyMapTitle}
                    isSaving={isSavingMoneyMapTitle}
                    onSave={saveMoneyMapTitle}
                />
                {fetchedMoneyMap.status != null && (
                    <MoneyMapStatusChangeConfirmModal
                        isOpen={isConfirmModalOpen}
                        onClose={() => {
                            setIsConfirmModalOpen(false);
                        }}
                        status={fetchedMoneyMap.status}
                        onPressConfirm={async () => {
                            if (
                                fetchedMoneyMap.status === MoneyMapStatus.DRAFT
                            ) {
                                await onPressPublish();
                            } else if (
                                fetchedMoneyMap.status ===
                                MoneyMapStatus.PREVIEW
                            ) {
                                await onPressFinalize();
                            }
                        }}
                    />
                )}
                <TooltipGroupProvider tooltipGroupName="money-mapper-header" />
                <TooltipGroupProvider tooltipGroupName="money-mapper-left-nav" />
            </View>
            {typeof document !== 'undefined' &&
                createPortal(
                    <DragOverlay dropAnimation={null}>
                        {activePreset != null && (
                            <PresetTileDragOverlay preset={activePreset} />
                        )}
                    </DragOverlay>,
                    document.body,
                )}
        </DndContext>
    );
};
